import { ChevronLeft, ChevronRight } from 'lucide-react';
import type { ReactNode } from 'react';
import { formatNumber } from '../lib/format';
import { pagerItems, shownRange } from './pager-items';
import styles from './Pager.module.css';

interface PagerProps {
  readonly page: number;
  readonly pageCount: number;
  readonly pageSize: number;
  readonly rowCount: number;
  readonly total: number;
  readonly onPage: (page: number) => void;
}

/** "Showing 1–50 of 1,284" and the numbered pages (mockups Main.dc.html, Jobs.dc.html). */
export function Pager({ page, pageCount, pageSize, rowCount, total, onPage }: PagerProps): ReactNode {
  const { from, to } = shownRange(page, pageSize, rowCount);
  return (
    <nav className={styles.pager} aria-label="Pages">
      <span className={styles.shown}>
        Showing {formatNumber(from)}–{formatNumber(to)} of {formatNumber(total)}
      </span>
      <span className={styles.grow} />
      {pageCount > 1 && (
        <>
          <button type="button" className={styles.page} aria-label="Previous page" disabled={page <= 1} onClick={() => onPage(page - 1)}>
            <ChevronLeft size={14} aria-hidden />
          </button>
          {pagerItems(page, pageCount).map((item, index) =>
            item === 'gap' ? (
              <span key={`gap-${index}`} className={styles.gap} aria-hidden>
                …
              </span>
            ) : (
              <button key={item} type="button" className={styles.page} data-on={item === page || undefined} aria-current={item === page ? 'page' : undefined} onClick={() => onPage(item)}>
                {formatNumber(item)}
              </button>
            ),
          )}
          <button type="button" className={styles.page} aria-label="Next page" disabled={page >= pageCount} onClick={() => onPage(page + 1)}>
            <ChevronRight size={14} aria-hidden />
          </button>
        </>
      )}
    </nav>
  );
}
